import {
  CONTRACT_VERSION,
  CONTRACT_SUPPORTED_VERSIONS,
  REMOTE_SOURCE,
  SHELL_SOURCE,
  type PayloadByType,
  createLifecycleGuard,
  createRequestResponseRegistry,
  createResettableDeferred,
  getHandshakeResponse,
  isContractMessageFromSource,
  isShellToRemoteMessage,
  type AdapterConfig,
  type RemoteMessage,
  type RemoteToShellMessage,
  type ShellToRemoteMessage,
} from "@claudafinil/shared/contract";

export interface ConnectionReadyInfo {
  version: number;
  platform?: AdapterConfig["platform"];
  extensionVersion?: string;
}

export type ConnectionState =
  | "connecting"
  | "connected"
  | "rejected"
  | "timeout";

export interface ShellMessengerOptions {
  requestTimeoutMs?: number;
  handshakeTimeoutMs?: number;
  supportedVersions?: number[];
}

export interface ShellMessenger {
  sendToShell(message: RemoteToShellMessage): void;
  onShellMessage(handler: (message: ShellToRemoteMessage) => void): () => void;
  sendRequest<
    TResponsePayload = unknown,
    TType extends RemoteToShellMessage["type"] = RemoteToShellMessage["type"],
  >(
    type: TType,
    payload: PayloadByType<RemoteToShellMessage, TType>,
  ): Promise<TResponsePayload>;
  connect(): Promise<ConnectionReadyInfo>;
  destroy(): void;
  readonly connectionReady: Promise<ConnectionReadyInfo>;
  readonly connectionState: ConnectionState;
}

export function createShellMessenger({
  requestTimeoutMs = 7000,
  handshakeTimeoutMs = 10000,
  supportedVersions = [...CONTRACT_SUPPORTED_VERSIONS],
}: ShellMessengerOptions = {}): ShellMessenger {
  const handlers = new Set<(message: ShellToRemoteMessage) => void>();
  const requestRegistry = createRequestResponseRegistry({
    timeoutMs: requestTimeoutMs,
    timeoutError: (requestType) =>
      new Error(`Timeout waiting for ${requestType} response`),
  });
  const lifecycle = createLifecycleGuard("Shell messenger");
  const connection = createResettableDeferred<ConnectionReadyInfo>();
  let state: ConnectionState = "connecting";
  let handshakeTimeoutId: ReturnType<typeof setTimeout> | undefined;

  const postMessage = (message: RemoteMessage): void => {
    lifecycle.assertActive();

    window.parent.postMessage(message, "*");
  };

  const sendToShell = (message: RemoteToShellMessage): void => {
    postMessage(message);
  };

  const resetConnection = (): void => {
    state = "connecting";
    if (handshakeTimeoutId) {
      clearTimeout(handshakeTimeoutId);
    }

    connection.reset();

    handshakeTimeoutId = setTimeout(() => {
      state = "timeout";
      connection.reject(new Error("Timed out waiting for HANDSHAKE_ACK"));
    }, handshakeTimeoutMs);
  };

  const handleHandshakeResponse = (message: ShellToRemoteMessage): void => {
    const handshakeResponse = getHandshakeResponse(message);
    if (!handshakeResponse) {
      return;
    }

    if (handshakeTimeoutId) {
      clearTimeout(handshakeTimeoutId);
      handshakeTimeoutId = undefined;
    }

    if (handshakeResponse.type === "ack") {
      state = "connected";
      const readyInfo: ConnectionReadyInfo = {
        version: handshakeResponse.selectedVersion,
      };
      if (handshakeResponse.platform !== undefined) {
        readyInfo.platform = handshakeResponse.platform;
      }
      if (handshakeResponse.extensionVersion !== undefined) {
        readyInfo.extensionVersion = handshakeResponse.extensionVersion;
      }
      connection.resolve(readyInfo);
      return;
    }

    if (handshakeResponse.type === "reject") {
      state = "rejected";
      connection.reject(
        new Error(
          `Handshake rejected. shellVersion=${handshakeResponse.shellVersion}, minSupported=${handshakeResponse.minSupported}`,
        ),
      );
    }
  };

  const onMessage = (event: MessageEvent): void => {
    if (event.source !== window.parent) return;
    if (!isContractMessageFromSource(event.data, SHELL_SOURCE)) return;
    if (!isShellToRemoteMessage(event.data)) {
      console.warn(
        `[ShellMessenger] Dropping invalid shell message: ${String(event.data.type)}`,
      );
      return;
    }

    const message = event.data;
    handleHandshakeResponse(message);

    if (message.requestId) {
      requestRegistry.resolve(message.requestId, message.payload);
    }

    for (const handler of handlers) {
      handler(message);
    }
  };

  window.addEventListener("message", onMessage);

  const onShellMessage = (
    handler: (message: ShellToRemoteMessage) => void,
  ): (() => void) => {
    handlers.add(handler);
    return () => {
      handlers.delete(handler);
    };
  };

  const sendRequest = <
    TResponsePayload = unknown,
    TType extends RemoteToShellMessage["type"] = RemoteToShellMessage["type"],
  >(
    type: TType,
    payload: PayloadByType<RemoteToShellMessage, TType>,
  ): Promise<TResponsePayload> =>
    new Promise((resolve, reject) => {
      const requestId = crypto.randomUUID();
      requestRegistry.register({
        requestId,
        requestType: type,
        resolve,
        reject,
      });

      postMessage({
        source: REMOTE_SOURCE,
        type,
        version: CONTRACT_VERSION,
        requestId,
        payload,
      });
    });

  const connect = async (): Promise<ConnectionReadyInfo> => {
    resetConnection();

    sendToShell({
      source: REMOTE_SOURCE,
      type: "HANDSHAKE",
      version: CONTRACT_VERSION,
      payload: {
        supportedVersions: [...supportedVersions],
      },
    });

    return connection.promise;
  };

  const destroy = (): void => {
    lifecycle.destroy(() => {
      window.removeEventListener("message", onMessage);
      handlers.clear();

      if (handshakeTimeoutId) {
        clearTimeout(handshakeTimeoutId);
        handshakeTimeoutId = undefined;
      }

      requestRegistry.rejectAll(
        () => new Error("Shell messenger destroyed before response"),
      );
    });
  };

  return {
    sendToShell,
    onShellMessage,
    sendRequest,
    connect,
    destroy,
    get connectionReady(): Promise<ConnectionReadyInfo> {
      return connection.promise;
    },
    get connectionState(): ConnectionState {
      return state;
    },
  };
}
